/**
 * Human-readable rendering for operation results.
 *
 * Records are printed as aligned key/value blocks rather than a table: a Relay
 * global id runs to sixty characters, so any table wide enough to hold one is
 * unreadable and any table that fits has truncated the one value the caller
 * needs to paste into the next command. `--json` remains the machine path.
 */
const INDENT = '  ';

interface Connection {
  nodes: unknown[];
  totalCount?: number;
  pageInfo?: { hasNextPage?: boolean; endCursor?: string | null };
}

const CONNECTION_PLUMBING = new Set([
  'edges',
  'nodes',
  'pageInfo',
  'totalCount',
  'approximateTotalCount',
  '__typename',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The edge's own fields — a reveal report, a distance — are folded into the
 * node they describe, after its fields, so a row reads as one block.
 */
function rowOf(edge: unknown): unknown {
  if (!isRecord(edge) || !isRecord(edge.node)) return isRecord(edge) ? edge.node : edge;
  const { node, cursor: _cursor, __typename: _typename, ...extras } = edge;
  const said = Object.entries(extras).filter(
    ([, value]) => value != null && !(Array.isArray(value) && value.length === 0),
  );
  return { ...node, ...Object.fromEntries(said) };
}

function asConnection(value: unknown): Connection | null {
  if (!isRecord(value)) return null;
  if (Array.isArray(value.nodes)) return value as unknown as Connection;
  if (Array.isArray(value.edges)) return { ...value, nodes: value.edges.map(rowOf) } as unknown as Connection;

  return null;
}

/** An ISO timestamp keeps its time of day, down to the second; a bare date stays a date. */
function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 19).replace('T', ' ');
  }

  return String(value);
}

function isFlatRecord(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && Object.values(value).every((v) => !isRecord(v) && !Array.isArray(v));
}

/**
 * A list of small flat records — one status per dimension, one match per
 * filter — is laid out as aligned sub-rows under its key, values side by side.
 * The keys are dropped: they repeat on every row and the columns say enough.
 */
const isDeadline = (key: string) => /Until$/.test(key);

/**
 * `ownedUntil: 2026-12-11T23:59:59Z` reads as `until 2026-12-11 23:59:59`: the
 * key names the relation, so it is folded into the value where the column has
 * no header.
 */
function formatDeadline(value: unknown): string {
  return typeof value === 'string' && value !== '' ? `until ${formatScalar(value)}` : '—';
}

function formatFlatRows(rows: Record<string, unknown>[], indent: string): string {
  const keys = Object.keys(rows[0]).filter((key) => key !== '__typename');
  const ordered = [...keys.filter((key) => !isDeadline(key)), ...keys.filter(isDeadline)];
  const cells = rows.map((row) =>
    ordered.map((key) => (isDeadline(key) ? formatDeadline(row[key]) : formatScalar(row[key]))),
  );
  const widths = ordered.map((_, column) => Math.max(...cells.map((row) => (row[column] ?? '').length)));

  return cells
    .map((row) =>
      row.map((cell, column) => (column === row.length - 1 ? cell : cell.padEnd(widths[column]))).join('  '),
    )
    .join(`\n${indent}`);
}

/**
 * A nested collection is summarised rather than expanded: expanding it inline
 * loses the alignment that makes the block readable, and the count is what a
 * caller scanning a list actually wants.
 */
function summarise(key: string, value: unknown): string | null {
  const connection = asConnection(value);
  if (connection) {
    const count = connection.totalCount ?? connection.nodes.length;
    return `${count} ${count === 1 ? 'entry' : 'entries'}`;
  }
  if (Array.isArray(value)) return value.length === 0 ? 'none' : `${value.length} items`;
  if (isRecord(value)) {
    const label = value.name ?? value.sub ?? value.id;
    return label === undefined ? '{…}' : formatScalar(label);
  }

  return null;
}

/**
 * A row in a list is titled by its id: the one value every row has, whatever
 * the caller may see of it, and the one the next command is pasted. The id
 * line is then not repeated below.
 */
function titleOf(record: Record<string, unknown>): string | null {
  return typeof record.id === 'string' && record.id !== '' ? record.id : null;
}

function formatRecord(record: Record<string, unknown>, indent: string, titled = false): string {
  const title = titled ? titleOf(record) : null;
  const isFlatRows = (value: unknown) => Array.isArray(value) && value.length > 0 && value.every(isFlatRecord);
  const entries = Object.entries(record)
    .filter(([key]) => key !== '__typename' && (title === null || key !== 'id'))
    .sort(([, a], [, b]) => Number(Array.isArray(a)) - Number(Array.isArray(b)));
  const width = Math.max(0, ...entries.map(([key]) => key.length));
  const valueIndent = `${indent}${''.padEnd(width)}  `;

  const body = entries
    .map(([key, value]) => {
      const rendered = isFlatRows(value)
        ? formatFlatRows(value as Record<string, unknown>[], valueIndent)
        : (summarise(key, value) ?? formatScalar(value));
      return `${indent}${key.padEnd(width)}  ${rendered}`;
    })
    .join('\n');

  return title === null ? body : `${indent.slice(INDENT.length)}${title}\n${body}`;
}

function formatFooter(connection: Connection): string | null {
  const shown = connection.nodes.length;
  const total = connection.totalCount;
  const more = connection.pageInfo?.hasNextPage === true;

  if (total === undefined && !more) return null;
  if (total !== undefined && total === shown && !more) return null;

  const counted = total === undefined ? `${shown}` : `${shown} of ${total}`;
  return more ? `${counted} — more available, pass --after ${connection.pageInfo?.endCursor}` : counted;
}

/**
 * What a connection says about the page as a whole — a reveal's bill — printed
 * after the list, each summary as its own titled block.
 */
function formatConnectionExtras(connection: Record<string, unknown>): string[] {
  return Object.entries(connection)
    .filter(([key, value]) => !CONNECTION_PLUMBING.has(key) && value != null)
    .map(([key, value]) =>
      isRecord(value) ? `${key}\n${formatRecord(value, INDENT)}` : `${key}  ${formatScalar(value)}`,
    );
}

/** Renders one operation result. Returns null when there is nothing to show. */
export function formatResult(result: unknown): string | null {
  const connection = asConnection(result);
  if (connection) {
    if (connection.nodes.length === 0) return 'No results.';

    const blocks = connection.nodes.map((node) =>
      isRecord(node) ? formatRecord(node, INDENT, true) : `${INDENT}${formatScalar(node)}`,
    );
    const tail = [formatFooter(connection), ...formatConnectionExtras(result as Record<string, unknown>)].filter(
      (part): part is string => part !== null,
    );

    return [blocks.join('\n\n'), ...tail].join('\n\n');
  }

  if (Array.isArray(result)) {
    if (result.length === 0) return 'No results.';

    return result.map((item) => (isRecord(item) ? formatRecord(item, INDENT, true) : formatScalar(item))).join('\n\n');
  }

  if (isRecord(result)) return formatRecord(result, '');

  return result === null || result === undefined ? null : formatScalar(result);
}
